import { DefaultSeoProps } from 'next-seo';

import { BRAND } from '../utils/brand-title';
import { isProductionDeployment } from '../utils/deployment-environment';

/**
 * Absolute URL of a file in worker-app's `public/utils-assets/`.
 *
 * Two things are easy to get wrong here and neither one shows up on screen.
 *
 * The host is not `BASE_URL`: that is `https://www.restworld.it` in production,
 * and `www` is the marketing site, which does not carry this app's `public/`
 * folder. It has to be the app's own host, even for a page `www` serves: it
 * proxies `/login`, not `/utils-assets/`.
 *
 * And the environment is read through `isProductionDeployment` rather than by
 * comparing `NEXT_PUBLIC_APP_ENV` here: that helper reads `VERCEL_ENV` too, so
 * it still answers correctly when the variable a human has to remember is
 * missing. Writing the comparison out again would be a second copy of the rule,
 * free to drift from the first.
 */
const workerAppAssetUrl = (fileName: string): string =>
  `${
    isProductionDeployment() ? 'https://worker.restworld.it' : 'https://dev.worker.restworld.it'
  }/utils-assets/${fileName}`;

/**
 * The Open Graph image a page falls back to when it declares none of its own.
 */
export const ogDefaultImageUrl = (): string => workerAppAssetUrl('og_restworld.png');

/**
 * The Open Graph image of `/login`.
 *
 * That page is where every passwordless access link lands, so its preview is the
 * card a worker sees in WhatsApp or in their mail client before deciding whether
 * the link is safe to open. The brand default said nothing about what the link
 * does; this one carries the promise itself ("Accedi in un click. Senza
 * password."), which is what the page is for.
 */
export const ogLoginImageUrl = (): string => workerAppAssetUrl('og_login.png');

// No `titleTemplate` on purpose. next-seo 5.15.0 keeps the template in a
// module-level `defaults` object, so the first page that renders with one makes
// every later page in the same server process inherit it, whether it asked for
// it or not. The brand suffix is applied once, explicitly, in wrapper-layout.
export const defaultSEO: DefaultSeoProps = {
  title: BRAND,
  description:
    'Restworld connette chi cerca lavoro nella ristorazione con i migliori locali. Offerte verificate, stipendi trasparenti e condizioni chiare.',
  twitter: {
    cardType: 'summary_large_image',
  },
  openGraph: {
    type: 'website',
    // Absolute, and to a file that exists. `/og_restworld.png` was neither: no
    // scraper resolves a relative `og:image`, and the path answered 404 on both
    // `www.restworld.it` and `worker.restworld.it` (checked 02/09/2026) because
    // the asset is served under `/utils-assets/`. Every page that does not set
    // an image of its own was therefore declaring a preview that does not load,
    // which reads exactly like a working one in the HTML.
    images: [{ url: ogDefaultImageUrl(), width: 2400, height: 1260 }],
  },
};
